import Configuration from '@/agilis/util/Configuration'
import Vue from 'vue'
import { i18nLoadLanguageAsync } from '@/i18n/i18n'
import L from 'lodash'
import store from '@/store'

/**
 * The default user profile structure
 *
 * id: @String the user identification, e.g. "mvampa"
 * settings: @Object the user settings
 *    lang: @String the user interface language, e.g. "it"
 *    filters: @Array the list of custom table filters and views, e.g.
 *      [{
 *        filterTerms: @Array the list of filter term objects, e.g.
 *          [{
 *            operator: @String the filter operator, e.g. "EQUALS",
 *            value: @String the filter value, e.g. "VAMPA",
 *            field: @String the field for the filter condition, e.g. "assignee.lastName"
 *          }],
 *        id: @String the filter id, e.g. "filter_t6zbbbuu7",
 *        name: @String the filter name, e.g. "My filter",
 *        route: @String the filter route, e.g. "/api/sales/workspace/sorintlab.opportunity/tracker/opportunity",
 *        sortTerms: @Array the list of sort term objects, e.g.
 *          [{
 *            sortField: @String the field for the sorting condition, e.g. "dueDate",
 *            sortType: @String the sort mode type, e.g. "ASC"
 *          }]
 *      }]
 *    loadingFeedback: @Boolean the flag to enable/disable the loading spinners when fetching data
 *    tours: @Array the list of completed/skipped tours by the user, e.g.
 *      [{
 *        id: @String the tour identification, e.g. "agilis.AppSelector",
 *        version: @String the tour version, e.g. "1.0.0"
 *      }]
 */
const defaultUserProfileDTO = {
  id: '',
  settings: {
    lang: 'en',
    filters: [],
    loadingFeedback: true,
    pushMessagesEnable: false,
    tours: []
  }
}

export default {
  namespaced: true,

  state: {
    userId: '',
    roles: [],
    privileges: [],
    privilegeExclusions: [],
    userProfile: {},
    userIsAdmin: false,
    showSplash: true,
    showTours: false
  },

  getters: {
    useDarkColors (state) {
      return state.userProfile?.settings?.useDarkColors
    },
    userId (state) {
      return state.userId
    },
    userProfile (state) {
      return state.userProfile
    },
    userLanguage (state) {
      return state?.userProfile?.settings?.lang
    },
    privileges (state) {
      return state.privileges
    },
    userIsAdmin (state) {
      return state.userIsAdmin
    },
    showSplash (state) {
      return state.showSplash
    },
    showTours (state) {
      return state.showTours
    },
    userTours (state) {
      return state?.userProfile?.settings?.tours
    },
    filters (state) {
      return state.userProfile?.settings?.filters || []
    },
    loadingFeedback (state) {
      return state.userProfile?.settings?.loadingFeedback
    },
    pushMessagesEnable (state) {
      return state.userProfile?.settings?.pushMessagesEnable &&
        Configuration.auth.userCanAccessProfileSetting('pushMessagesEnable')
    },
    theme (state) {
      return state.userProfile?.settings?.theme
    }
  },

  mutations: {
    setShowTours: function (state, showTours) {
      state.showTours = showTours
    },
    setUserId: function (state, userId) {
      state.userId = userId
    },
    setUserIsAdmin: function (state, userIsAdmin) {
      state.userIsAdmin = userIsAdmin
    },
    setShowSplash: function (state, showSplash) {
      state.showSplash = showSplash
    },
    setRoles: function (state, roles) {
      state.roles = roles
    },
    setPrivileges: function (state, privileges) {
      state.privileges = privileges
    },
    setPrivilegeExclusions: function (state, privilegeExclusions) {
      state.privilegeExclusions = privilegeExclusions
    },
    setUserProfile: function (state, userProfile) {
      state.userProfile = userProfile
      if (userProfile) {
        i18nLoadLanguageAsync(userProfile.settings.lang)
      }
    }
  },

  actions: {
    setUserId (context, userId) {
      // 'store' is the store, 'context' is the store module
      context.commit('setUserId', userId)
      context.dispatch('getUserProfile')
      context.dispatch('calculatePrivileges')
      store.dispatch('configurationStore/auth/setApplications')
    },
    getUserProfile (context) {
      const getPromise = new Promise(function (resolve) {
        const url = Vue.axios.agilisUrlUserProfile(context.getters.userId)
        Vue.axios
          .get(url, Vue.axios.agilisRequestConfig(url, window.envmap.VUE_APP_AGILIS_CONFIGURATOR_ENDPOINT))
          .then((response) => {
            if (!L.isEmpty(response.data)) {
              context.commit('setUserProfile', L.merge({}, defaultUserProfileDTO, {
                id: context.getters.userId,
                ...response.data
              }))
              Vue.prototype.$debug('userprofile loaded')
            } else {
              const defaultUserProfile = calculateUserProfile(context)
              const userProfile = L.merge({}, defaultUserProfile, {
                id: context.getters.userId
              })
              context.commit('setUserProfile', userProfile)
              context.dispatch('saveUserProfile', userProfile)
            }
            resolve()
          })
          .catch((error) => {
            context.dispatch('notificationStore/notifyError', error, { root: true })
            resolve()
          })
      })
      return getPromise
    },
    calculatePrivileges (context) {
      let userPrivilegeSet = new Set()
      let userPrivilegeExclusionSet = new Set()
      const applications = context.rootGetters['configurationStore/applications']
      for (const appId in applications) {
        const appConfig = applications[appId]
        const appRoles = Configuration.auth.mergeRoles(appConfig.roles)

        Configuration.auth.addUserPrivilegesFromWorkspaces(
          context.state.userId, appId, appRoles, appConfig.workspaces, userPrivilegeSet, userPrivilegeExclusionSet)
      }
      context.commit('setPrivileges', Array.from(userPrivilegeSet))
      context.commit('setPrivilegeExclusions', Array.from(userPrivilegeExclusionSet))
      context.commit('setUserIsAdmin', Configuration.auth.userHasPrivilege('/admin/*'))
      Vue.prototype.$debug('calculatePrivileges', userPrivilegeSet.size, userPrivilegeExclusionSet.size)
    },
    async saveUserProfile (context, userProfile) {
      return saveProfile(context, userProfile)
    },
    async setFilters (context, filters = []) {
      // TODO: migliorare questa parte, vedere nella doc VEUX cosa suggeriscono
      const userProfile = JSON.parse(JSON.stringify(context.getters.userProfile)) // trick to avoid change the state directly

      // update the setting and commit
      userProfile.settings.filters = filters
      return saveProfile(context, userProfile)
    },
    async completeTour (context, tour) {
      const userProfile = JSON.parse(JSON.stringify(context.getters.userProfile)) // trick to avoid change the state directly

      // update the setting and commit
      if (!userProfile.settings.tours.some(userTour =>
        userTour.id === tour.id &&
        userTour.version === tour.version
      )) {
        userProfile.settings.tours.push(tour)
      }
      return saveProfile(context, userProfile)
    }
  }
}

async function saveProfile (context, userProfile) {
  const savePromise = new Promise((resolve, reject) => {
    const url = Vue.axios.agilisUrlUserProfile(context.getters.userId)
    Vue.axios.post(url, userProfile, Vue.axios.agilisRequestConfig(url, window.envmap.VUE_APP_AGILIS_CONFIGURATOR_ENDPOINT))
      .then(async (response) => {
        await context.dispatch('getUserProfile')
        resolve()
      })
      .catch((error) => {
        context.dispatch('notificationStore/notifyError', error, { root: true })

        reject(error)
      })
  })
  return savePromise
}

function calculateUserProfile (context) {
  let defaultUserProfile = defaultUserProfileDTO
  const userProfileFilters = []
  const applications = context.rootGetters['configurationStore/applications']
  for (const appId in applications) {
    const appDefaultUserProfile = applications[appId].defaultUserProfile
    if (appDefaultUserProfile) {
      userProfileFilters.push(...appDefaultUserProfile.settings.filters)

      defaultUserProfile = L.merge({}, defaultUserProfile, {
        ...appDefaultUserProfile,
        settings: {
          lang: appDefaultUserProfile.settings.lang,
          filters: userProfileFilters
        }
      })
    }
  }
  return defaultUserProfile
}
